import { useEffect, useRef, useState } from 'react';
import { useFormikContext } from 'formik';
import { Grid, MenuItem, Switch, IconButton, Icon, Tooltip } from '@mui/material';
import { portalLanguagesData } from 'app/appSettings';
import { TextFieldWithFormik } from './TextFieldWithFormik';
import TranslationConnectionsDialog from 'app/views/internal/TranslationConnectionsDialog';
import { Trans, t } from '@lingui/macro';
import { myI18n } from 'translation/I18nConnectedProvider';

interface FormValues {
  [fieldName: string]: {
    [language: string]: string;
  };
}

const FormikLanguageTextField = ({
  name,
  langKey,
  disabled,
  maxCharacters,
  label,
  ...props
}) => {
  const { values, setFieldValue } = useFormikContext<FormValues>();
  const value = values[name]?.[langKey] || '';
  const [localValue, setLocalValue] = useState(value);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setFieldValue(name, { ...values[name], [langKey]: localValue });
    }, 700);

    return () => clearTimeout(timerRef.current);
  }, [localValue]);

  const handleChange = (e) => {
    setLocalValue(e.target.value);
  };

  return (
    <TextFieldWithFormik
      name={`${name}.${langKey}`}
      value={localValue}
      onChange={handleChange}
      disabled={disabled}
      inputProps={{ maxLength: maxCharacters }}
      topLabel={label}
      {...props}
    />
  );
};

const ConfigureMultilanguageTextFieldWithFormik = ({
  name,
  label,
  maxCharacters,
  disabled,
  allowConnectingToPortalTranslation = true,
  onlySelectTranslation = false,
  resetToExisting = false,
  values,
  setFieldValue,
  ...props
}) => {
  const [selectedLanguageKey, setSelectedLanguageKey] = useState('en');
  const [showInColumn, setShowInColumn] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const isId = values?.portalTranslationId;

  const saveTranslationConnection = (id) => {
    const newValue = { portalTranslationId: id, ...(resetToExisting ? values : {}) };
    setFieldValue(name, newValue);
  };

  const clearTranslationConnection = () => {
    setFieldValue(name, { portalTranslationId: '', ...(resetToExisting ? values : {}) });
  };

  const toggleViewMode = () => {
    setShowInColumn(!showInColumn);
  };

  const keyToLabel = {
    'en': <Trans>English</Trans>,
    'fr': <Trans>French</Trans>,
    'iu-cans': <Trans>Inuktitut</Trans>
  }

  return (
    <>
      <Grid container wrap="nowrap" alignItems="center" spacing={2}>
        {!isId && !onlySelectTranslation && (
          <Grid 
            item 
            style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              justifyContent: 'center', 
              alignItems: 'center', 
              }}
            >
            {showInColumn ? <Trans>MULTILANGUAGE_EDIT_COLUMNS</Trans> : <Trans>MULTILANGUAGE_EDIT_DROPDOWN</Trans>}
            <Switch checked={showInColumn} onChange={toggleViewMode} color="primary" />
          </Grid>
        )}

        {(allowConnectingToPortalTranslation || onlySelectTranslation) && (
          <Grid item style={{ marginTop: 14 }}>
            {isId ? (
              <Tooltip title={<Trans>MULTILANGUAGE_EDIT_CLEAR_CONNECTION</Trans>}>
                <IconButton onClick={clearTranslationConnection} size="small">
                  <Icon>clear</Icon>
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title={<Trans>MULTILANGUAGE_EDIT_CONNECT_TO_PORTAL_TRANSLATION</Trans>}>
                <IconButton onClick={() => setDialogOpen(true)} size="small">
                  <Icon>language</Icon>
                </IconButton>
              </Tooltip>
            )}
          </Grid>
        )}

        {showInColumn && !isId ? (
          <Grid container direction="column" justifyContent="center" style={{ paddingLeft: 8 }}>
            {label && (
              <h6 style={{ marginBottom: 24, marginLeft: 16, marginTop: 24 }}>
                {label}
              </h6>
            )}
            {Object.keys(portalLanguagesData).map((langKey) => (
              <Grid item xs key={langKey} style={{ marginBottom: 10, marginLeft: 6 }}>
                <FormikLanguageTextField
                  label={keyToLabel[langKey]}
                  name={name}
                  langKey={langKey}
                  disabled={disabled}
                  maxCharacters={maxCharacters}
                  InputProps={{ style: { height: 56 } }}
                  size='medium'
                  {...props}
                />
              </Grid>
            ))}
          </Grid>
        ) : (
          <>
            <Grid item xs>
              <FormikLanguageTextField
                name={name}
                langKey={selectedLanguageKey}
                label={label}
                disabled={disabled}
                maxCharacters={maxCharacters}
                InputProps={{ style: { height: 56 } }}
                size='medium'
                {...props}
              />
            </Grid>
            {!isId && !onlySelectTranslation && (
              <Grid item style={{ minWidth: 150 }}>
                <TextFieldWithFormik
                  name="language"
                  select
                  variant="outlined"
                  fullWidth
                  size='medium'
                  value={selectedLanguageKey}
                  onChange={(e) => setSelectedLanguageKey(e.target.value)}
                  topLabel={myI18n._(t`Language`)}
                  InputProps={{ style: { height: 56 } }}
                >
                  {Object.keys(portalLanguagesData).map((langKey) => (
                    <MenuItem key={langKey} value={langKey}>
                      {portalLanguagesData[langKey].label}
                    </MenuItem>
                  ))}
                </TextFieldWithFormik>
              </Grid>
            )}
          </>
        )}
      </Grid>

      <TranslationConnectionsDialog
        open={dialogOpen}
        handleClose={() => setDialogOpen(false)}
        saveTranslationConnection={saveTranslationConnection}
      />
    </>
  );
};

export default ConfigureMultilanguageTextFieldWithFormik;
